import { Bell, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, ListGroup, Overlay, Popover, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { UserNotification } from '../../service/api';
import useNotificationsStore from '../../store/notifications';

/** Polling interval for new notification count (30 seconds) */
const POLL_INTERVAL_MS = 30_000;

/**
 * Renders a translated notification message based on type and params
 */
function NotificationMessage({
  notification,
}: {
  notification: UserNotification;
}) {
  const { t } = useTranslation();
  const { type, params } = notification;

  switch (type) {
    case 'card_delivered':
      return (
        <>
          {t('notifications.cardDelivered', {
            managerName: params.managerName,
            cardName: params.cardName,
          })}
        </>
      );
    case 'effect_activated': {
      // Map effectType to translated effect description using existing i18n keys
      const effectTypeKey = params.effectType as string;
      const effectDescriptions: Record<string, string> = {
        set_price: t('effects.setPriceTo', { value: params.value }),
        adjust_all_prices_percent: t('effects.allPricesPercent', {
          value: params.value,
        }),
        adjust_item_price_percent: t('effects.itemPricePercent', {
          value: params.value,
        }),
      };
      const effectDescription =
        effectDescriptions[effectTypeKey] ?? t('effects.unknownEffect');

      // Map durationType to translated duration string
      const durationType = params.durationType as string;
      const duration =
        durationType === 'end_of_day'
          ? t('effects.endOfDay')
          : t('notifications.forMinutes', {
              count: Number(params.durationMinutes) || 0,
            });

      // Include target item name when available
      const targetItemName = params.targetItemName as string | undefined;

      return (
        <>
          {targetItemName
            ? t('notifications.effectActivatedWithTarget', {
                effectDescription,
                duration,
                targetItemName,
              })
            : t('notifications.effectActivated', {
                effectDescription,
                duration,
              })}
        </>
      );
    }
    case 'purchase_rejected':
      return <>{t('notifications.purchaseRejected')}</>;
    case 'stickers_changed': {
      const stickerAmount = Number(params.amount) || 0;
      const translationKey =
        stickerAmount >= 0
          ? 'notifications.stickersAdded'
          : 'notifications.stickersRemoved';
      return (
        <>
          {t(translationKey, {
            managerName: params.managerName,
            amount: Math.abs(stickerAmount),
          })}
        </>
      );
    }
    case 'achievement_reached':
      return (
        <>
          {t('notifications.achievementReached', {
            achievementName: params.achievementName,
          })}
        </>
      );
    case 'task_failed':
      return (
        <>
          {t('notifications.taskFailed', {
            managerName: params.managerName,
            taskName: params.taskName,
          })}
        </>
      );
    default:
      return <>{t('notifications.unknown')}</>;
  }
}

/**
 * Formats a DateTime for display in notification list
 */
function formatNotificationDate(createdAt: DateTime): string {
  if (!(createdAt instanceof DateTime)) {
    return '';
  }

  const now = DateTime.now();
  const diff = now.diff(createdAt, 'hours').hours;

  if (diff < 24) {
    return createdAt.toFormat('HH:mm');
  }

  if (diff < 24 * 7) {
    return createdAt.toRelative() ?? createdAt.toFormat('dd MMM HH:mm');
  }

  return createdAt.toFormat('dd MMM HH:mm');
}

/**
 * Bell icon with red badge showing count of new notifications.
 * Clicking opens a popover with the last 50 notifications.
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  const notifications = useNotificationsStore(state => state.notifications);
  const newCount = useNotificationsStore(state => state.newCount);
  const isLoading = useNotificationsStore(state => state.isLoading);
  const fetchNotifications = useNotificationsStore(
    state => state.fetchNotifications,
  );
  const fetchNewCount = useNotificationsStore(state => state.fetchNewCount);
  const markAllAsSeen = useNotificationsStore(state => state.markAllAsSeen);
  const hideNotification = useNotificationsStore(
    state => state.hideNotification,
  );

  // Poll for new count periodically
  useEffect(() => {
    fetchNewCount();
    const interval = setInterval(fetchNewCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNewCount]);

  const handleToggle = useCallback(() => {
    if (!show) {
      // Opening the popup: fetch notifications and mark as seen
      fetchNotifications().then(() => {
        markAllAsSeen();
      });
    }
    setShow(prev => !prev);
  }, [show, fetchNotifications, markAllAsSeen]);

  const handleHide = useCallback(
    (notificationId: string) => {
      hideNotification(notificationId);
    },
    [hideNotification],
  );

  return (
    <div ref={bellRef} style={{ position: 'relative', cursor: 'pointer' }}>
      <div onClick={handleToggle} aria-label={t('notifications.title')}>
        <Bell size={28} color="white" />
        {newCount > 0 && (
          <Badge
            bg="danger"
            pill
            style={{
              position: 'absolute',
              top: -6,
              right: -8,
              fontSize: '0.65rem',
              minWidth: '18px',
            }}
          >
            {newCount > 99 ? '99+' : newCount}
          </Badge>
        )}
      </div>

      <Overlay
        show={show}
        target={bellRef.current}
        placement="bottom-end"
        rootClose
        onHide={() => setShow(false)}
      >
        <Popover
          id="notifications-popover"
          style={{
            maxWidth: '360px',
            width: '360px',
            backgroundColor: 'var(--bs-primary)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
          }}
        >
          <Popover.Header
            as="h3"
            className="d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: 'var(--bs-secondary)',
              color: 'var(--bs-light)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
            }}
          >
            {t('notifications.title')}
          </Popover.Header>
          <Popover.Body
            style={{
              maxHeight: '500px',
              overflowY: 'auto',
              padding: 0,
              backgroundColor: 'var(--bs-primary)',
            }}
          >
            {isLoading && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            )}

            {!isLoading && notifications.length === 0 && (
              <div className="text-center text-white-50 py-3">
                {t('notifications.empty')}
              </div>
            )}

            {!isLoading && notifications.length > 0 && (
              <ListGroup variant="flush">
                {notifications.map(notification => (
                  <ListGroup.Item
                    key={notification._id}
                    className="d-flex justify-content-between align-items-start py-2 px-3"
                    style={{
                      backgroundColor:
                        notification.status === 'new'
                          ? 'var(--bs-secondary)'
                          : 'var(--bs-primary)',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div
                      className="flex-grow-1 me-2 text-white"
                      style={{ fontSize: '0.875rem' }}
                    >
                      <div>
                        <NotificationMessage notification={notification} />
                      </div>
                      <small className="text-white-50">
                        {formatNotificationDate(notification.createdAt)}
                      </small>
                    </div>
                    <Trash2
                      size={16}
                      className="flex-shrink-0 mt-1 text-white-50"
                      style={{ cursor: 'pointer' }}
                      onClick={e => {
                        e.stopPropagation();
                        handleHide(notification._id);
                      }}
                      aria-label={t('notifications.hide')}
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
}
